import { useState, useMemo } from "react";
import { reqFunction } from "utils/API";
import ConsumptionChartFilter from "./ConsumptionChartFilter";
import { DynamicOption } from "components/fields/SelectInput";
import { counterTagSelect } from "variables";
import { ProjectObject } from "views/projects";
import { getToday } from "utils/TimeUtiles";
import { useQuery } from "@tanstack/react-query";
import MixChart from "components/charts/MixChart";
import { useParams } from "react-router-dom";
import {
  renderComplexChartData,
  renderComplexChartOptions,
} from "utils/globalUtils";

interface ProjectConsumptionChartProps {
  projectData: [ProjectObject];
  isDashboard?: boolean; // if true show in the project dashboard
  tagDefault?: number;
  /** NEW: optional list of selected meter serials. If provided, chart will use these instead of project_id */
  selectedMeters?: string[];
}

const ProjectConsumptionChart = ({
  projectData,
  isDashboard = false,
  tagDefault = 0, // 0 is default for water
  selectedMeters = [],
}: ProjectConsumptionChartProps) => {
  const { projectId } = useParams();

  const [tag, setTag] = useState<DynamicOption>(counterTagSelect[tagDefault]);
  const [fromDate, setFromDate] = useState(getToday().jDate(1));
  const [tillDate, setTillDate] = useState(getToday().add(1, "day"));

  // payload values depend on whether meters are selected
  const water_meters = selectedMeters.length ? selectedMeters : null;
  const effectiveProjectId = selectedMeters.length ? null : projectId;

  const queryKeyMeters = useMemo(
    () => JSON.stringify(selectedMeters.slice().sort()),
    [selectedMeters]
  );

  const {
    data: consumptionsDatesData,
    isLoading: consumptionsDatesIsLoading,
    status: consumptionsDatesStatus,
    refetch: consumptionDatesRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll/consumption/date",
        {
          page: 1,
          count: 1000,
          project_id: effectiveProjectId,
          user_id: null,
          water_meters, // <— key change
          type_id: null,
          tag_id: tag.value,
          start_time: fromDate.format("YYYY-M-D"),
          end_time: tillDate.format("YYYY-M-D"),
        },
        "post"
      ),
    queryKey: [
      "consumptionsDates",
      effectiveProjectId,
      tag?.value,
      fromDate.format("YYYY-M-D"),
      tillDate.format("YYYY-M-D"),
      queryKeyMeters, // <— ensures refetch when you change selection
    ],
    // keep prior data to avoid flicker
    placeholderData: (prev) => prev as any,
    staleTime: 30_000,
  });

  return (
    <div className="projects-overview pt-4">
      {!isDashboard && (
        <div className="chart-statistics py-2">
          <ConsumptionChartFilter
            tagSelect={counterTagSelect}
            tag={tag}
            setTag={setTag}
            fromDate={fromDate}
            setFromDate={setFromDate}
            tillDate={tillDate}
            setTillDate={setTillDate}
            update={consumptionDatesRefetch}
            total={
              consumptionsDatesData?.data
                ? consumptionsDatesData?.data?.total
                : 0
            }
            tab="chart"
          />
        </div>
      )}
      {!consumptionsDatesIsLoading && consumptionsDatesStatus === "success" ? (
        <div className="chart-container mx-auto h-[450px] w-full min-w-[95%]">
          <MixChart
            chartData={renderComplexChartData(
              consumptionsDatesData.data,
              fromDate,
              tillDate
            )}
            chartOptions={renderComplexChartOptions(fromDate, tillDate)}
            chartTag={tag}
          />
        </div>
      ) : (
        <div>chart is loading</div>
      )}
    </div>
  );
};

export default ProjectConsumptionChart;
